import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, categories } from '@/mocks/articles';
import { OrganizationSchema, WebSiteSchema, WebPageSchema, BreadcrumbListSchema } from '@/components/base/SeoSchema';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const filteredCategoryArticles = articles.filter((a) => a.categorySlug === slug);
  const categoryArticles = filteredCategoryArticles.length ? filteredCategoryArticles : articles;

  const categoryName = currentCategory?.name || '分类';
  const categoryDesc = currentCategory?.description || '';

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-200/70">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-heading text-foreground-950 mb-3">分类未找到</h1>
            <p className="text-foreground-600 mb-6">该分类不存在或已被移除</p>
            <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const formatViews = (v: number): string => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return String(v);
  };

  return (
    <>
      <OrganizationSchema />
      <WebSiteSchema />
      <WebPageSchema
        title={`${categoryName} - 51在线吃瓜 | 51吃瓜官网全网最新`}
        description={categoryDesc}
        url={`http://smallbar.cn/category/${slug}`}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: 'http://smallbar.cn/' },
          { name: categoryName, url: `http://smallbar.cn/category/${slug}` },
        ]}
      />
      <Navbar />
      <main>
        {/* Category Header */}
        <section className="relative w-full min-h-[420px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20dramatic%20abstract%20warm%20toned%20background%20with%20coral%20and%20amber%20gradients%20blending%20into%20deep%20charcoal%20modern%20minimalist%20editorial%20style%20soft%20diffused%20lighting%20creating%20atmosphere%20suitable%20for%20an%20entertainment%20news%20category%20header%20no%20text%20no%20people%20just%20atmospheric%20color%20gradients&width=1600&height=600&seq=category-header-generic&orientation=landscape"
              alt={categoryName}
              title={`${categoryName} - 51在线吃瓜`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 py-20">
            <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-10">
              <div className="lg:w-2/5 text-left">
                <nav className="flex items-center gap-2 text-background-300 text-xs mb-6" aria-label="面包屑导航">
                  <Link to="/" className="hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
                  <i className="ri-arrow-right-s-line"></i>
                  <span className="text-background-50">{categoryName}</span>
                </nav>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading text-background-50 leading-tight mb-4">
                  {categoryName}
                  <br />
                  <span className="italic text-primary-400">最新爆料</span>
                </h1>
                <p className="text-background-200 text-base leading-relaxed mb-6">
                  {categoryDesc}
                </p>
                <div className="flex items-center gap-4">
                  <span className="text-background-300 text-sm">
                    共 <strong className="text-background-50">{categoryArticles.length}</strong> 篇文章
                  </span>
                </div>
              </div>
              <div className="lg:w-3/5">
                {categoryArticles.length > 0 && (
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src={categoryArticles[0].imageUrl}
                      alt={categoryName}
                      title={`${categoryName} - 51在线吃瓜`}
                      className="w-full h-72 object-cover object-top rounded-lg"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200/70 hover:text-foreground-950'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="text-3xl md:text-4xl font-heading text-foreground-950 leading-tight">
                  最新{categoryName}
                </h2>
              </div>
              <p className="text-foreground-600 text-sm hidden md:block">
                {categoryDesc}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {categoryArticles.length > 0 && (
                <div className="md:col-span-1">
                  <Link to={`/article/${categoryArticles[0].slug}`} className="block h-full">
                    <article className="bg-foreground-900 rounded-lg overflow-hidden group cursor-pointer h-full flex flex-col">
                      <div className="relative h-56 overflow-hidden">
                        <img
                          src={categoryArticles[0].imageUrl}
                          alt={categoryArticles[0].title}
                          title={`${categoryArticles[0].title} - 51在线吃瓜`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-foreground-900/80 to-transparent"></div>
                        <div className="absolute top-3 left-3">
                          <span className="px-2.5 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                            置顶
                          </span>
                        </div>
                      </div>
                      <div className="p-5 flex-1 flex flex-col justify-between">
                        <div>
                          <h3 className="text-background-50 text-lg font-medium leading-snug mb-2 group-hover:text-primary-400 transition-colors line-clamp-2">
                            {categoryArticles[0].title}
                          </h3>
                          <p className="text-background-400 text-sm leading-relaxed line-clamp-2">
                            {categoryArticles[0].excerpt}
                          </p>
                        </div>
                        <div className="flex items-center justify-between mt-4 text-xs text-background-500">
                          <span>{categoryArticles[0].date}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {formatViews(categoryArticles[0].views)}
                          </span>
                        </div>
                      </div>
                    </article>
                  </Link>
                </div>
              )}
              <div className={`${categoryArticles.length > 0 ? 'md:col-span-2' : 'md:col-span-3'} grid grid-cols-1 sm:grid-cols-2 gap-6`}>
                {categoryArticles.slice(1).map((article) => (
                  <Link key={article.id} to={`/article/${article.slug}`} className="block h-full">
                    <article className="bg-background-50 rounded-lg overflow-hidden group cursor-pointer h-full flex flex-col">
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          title={`${article.title} - 51在线吃瓜`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="px-2.5 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                            {article.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-4 flex-1 flex flex-col justify-between">
                        <div>
                          <h3 className="text-foreground-950 text-base font-medium leading-snug mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                            {article.title}
                          </h3>
                          <p className="text-foreground-600 text-sm leading-relaxed line-clamp-2">
                            {article.excerpt}
                          </p>
                        </div>
                        <div className="flex items-center justify-between mt-3 text-xs text-foreground-500">
                          <span>{article.date}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {formatViews(article.views)}
                          </span>
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            </div>

            {categoryArticles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200/70">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}